import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Animated, DeviceEventEmitter, RefreshControl, View} from 'react-native';
import theme from '@/style';
import globalStore from '@/services/global.state';
import Spin from '@basicComponents/spin';
import {debounce, setDataForSettled} from '@/utils';
import {useFocusEffect} from '@react-navigation/native';
import {sequentialStorage} from '@utils/Storage/SequentialStorage';
import {inApp, isPWA} from '@/utils/interface';
import {getBannerList} from '../home.service';
import {BannerListItem} from '../home.type';
import AnnaHomeHeader from './anna-home-header';
import AnnaDownloadBanner from './anna-download-banner';
import AnnaBanner from './anna-banner';
import AnnaFloatingTools from './anna-floating-tools';
import {useAnnaFreeLottery} from './anna-home.hooks';
import {useToChat} from '@/pages/home/im.hook';
import {SharedHomeHotBanner} from '../components/home-main-content';
import AnnaGoldArea from './anna-gold-area';
import AnnaHomeContent from './anna-home-content';
import {useHomeStore} from '../v2/store';
import {goTo} from '@/utils';
import {useLogin} from '@/utils/state-hooks';

const STORAGE_KEY = 'home_download_banner_closed';

const AnnaHome = () => {
  const [refreshing, setRefreshing] = useState(false);
  const [bannerList, setBannerList] = useState<BannerListItem[]>([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [downloadVisible, setDownloadVisible] = useState(false);
  const [currentScrollY, setCurrentScrollY] = useState(0);
  const [floatingVisible, setFloatingVisible] = useState(true);
  const [goldActive, setGoldActive] = useState(0);
  const scrollViewRef = React.useRef<any>(null);
  const floatingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const lastScrollYRef = useRef(0);
  const {renderLotteryModal, onPressLottery} = useAnnaFreeLottery();
  const login = useLogin();
  const {gameTypesList, getGameTypesList} = useHomeStore();

  const getList = (refresh = false) => {
    if (!refresh) {
      setPageLoading(true);
    }
    Promise.allSettled([getBannerList(2)])
      .then(([banner]) => {
        setDataForSettled(setBannerList, banner);
      })
      .finally(() => {
        if (!refresh) {
          DeviceEventEmitter.emit('startDownload');
        }
        setPageLoading(false);
        setRefreshing(false);
      });
  };

  useEffect(() => {
    getList();
    getGameTypesList();
    if (globalStore.isWeb && !inApp && !isPWA()) {
      setDownloadVisible(!localStorage.getItem(STORAGE_KEY));
    }
    return () => {
      if (floatingTimerRef.current) {
        clearTimeout(floatingTimerRef.current);
      }
    };
  }, [getGameTypesList]);

  useFocusEffect(
    useCallback(() => {
      const sub = globalStore.tokenSubject.subscribe(() => {
        globalStore.amountCheckOut.next({});
      });
      sequentialStorage.emit('updated');
      return () => {
        sub.unsubscribe();
      };
    }, []),
  );

  useToChat();

  const updateFloatingVisible = useCallback((y: number) => {
    lastScrollYRef.current = y;
    setFloatingVisible(false);
    if (floatingTimerRef.current) {
      clearTimeout(floatingTimerRef.current);
    }
    floatingTimerRef.current = setTimeout(() => {
      if (lastScrollYRef.current === y) {
        setFloatingVisible(true);
      }
    }, 300);
  }, []);

  const handleScrollTo = useMemo(
    () =>
      debounce((y: number) => {
        setCurrentScrollY(y);
      }, 50),
    [],
  );
  const bottomSpacerStyle = useMemo(() => ({height: 88}), []);
  const bannerBackgroundStyle = useMemo(
    () => ({
      backgroundPosition: '0 -5rem',
      backgroundSize: '100%',
    }),
    [],
  );

  const handleGoldAreaChange = useCallback(
    (index: number) => {
      if (gameTypesList[index]?.moduleType === 'SPORTS') {
        goTo('Sports');
        return;
      }
      setGoldActive(index);
    },
    [gameTypesList],
  );

  return (
    <Spin loading={pageLoading} style={[theme.flex.col, theme.fill.fill]}>
      <View
        style={[
          theme.fill.fill,
          theme.flex.col,
          theme.background.lightGrey,
          theme.position.rel,
        ]}>
        <AnnaHomeHeader
          login={login}
          onPressLottery={onPressLottery}
          isDownloadBannerVisible={downloadVisible}
        />
        <AnnaDownloadBanner
          visible={downloadVisible}
          onClose={() => {
            setDownloadVisible(false);
            if (globalStore.isWeb) {
              localStorage.setItem(STORAGE_KEY, 'true');
            }
          }}
        />
        <Animated.ScrollView
          ref={scrollViewRef}
          style={[theme.flex.flex1]}
          onScroll={e => {
            const nextY = e.nativeEvent.contentOffset.y;
            handleScrollTo(nextY);
            updateFloatingVisible(nextY);
          }}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => {
                setRefreshing(true);
                getList(true);
              }}
            />
          }>
          <div className="relative flex flex-col">
            <div
              className={`absolute left-0 right-0 rounded-b-[40%] bg-gradient-to-b from-[#F9B871] to-[#F11402] ${
                downloadVisible
                  ? '-top-[5.375rem] h-[13rem]'
                  : '-top-[2.75rem] h-[10rem]'
              }`}
            />
            <div className="bg-no-repeat" style={bannerBackgroundStyle}>
              <AnnaBanner bannerList={bannerList} />
            </div>
            <div className="bg-light-grey">
              <SharedHomeHotBanner />
              <AnnaGoldArea
                activeIndex={goldActive}
                list={gameTypesList}
                onChange={handleGoldAreaChange}
              />
              <AnnaHomeContent
                activeIndex={goldActive}
                scrollViewRef={scrollViewRef}
                scrollY={currentScrollY}
              />
            </div>
            <View style={bottomSpacerStyle} />
          </div>
        </Animated.ScrollView>
        <AnnaFloatingTools visible={floatingVisible} />
      </View>
      {renderLotteryModal}
    </Spin>
  );
};

export default AnnaHome;
